import { createORPCClient } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { ApiClient } from "@repo/api/client";
import { getSharedConfig, TENANT_SLUG_HEADER } from "@repo/config/sharedConfig";
import {
	createNamedContext,
	useRequiredContext,
} from "@repo/utils/hooks/useRequiredContext";
import { useMemo, type ReactNode } from "react";
import { absoluteRoute, Routes } from "../routing";
import { getTenantSlug, parseWrongTenantError } from "../tenant";
import { signOutAndReload } from "./AuthContext";

const sharedConfig = getSharedConfig(import.meta.env.MODE);

const ApiContext = createNamedContext<ApiClient | undefined>(
	"ApiClient",
	undefined,
);

type ApiClientProviderProps = {
	children: ReactNode;
};

export function ApiClientProvider({ children }: ApiClientProviderProps) {
	const client = useMemo((): ApiClient => {
		const link = new RPCLink({
			url: `${sharedConfig.serverUrl}/rpc`,
			async fetch(request, options) {
				const start = Date.now();
				const method = (options as RequestInit).method || "GET";
				const pathname = new URL(request.url).pathname;

				try {
					const headers = new Headers(request.headers);
					const tenantSlug = getTenantSlug();
					if (tenantSlug) {
						headers.set(TENANT_SLUG_HEADER, tenantSlug);
					}

					const response = await fetch(request, {
						...options,
						headers,
						credentials: "include",
					});

					const duration = Date.now() - start;
					const status = response.status;

					console.log("API Request", {
						method,
						pathname,
						status,
						duration,
						success: status >= 200 && status < 300,
					});

					// Only WRONG_TENANT is handled here. Any other 409 is a normal
					// conflict for the caller to surface, and treating them alike would
					// silently sign people out on an unrelated error.
					const wrongTenant =
						status === 409
							? await wrongTenantErrorFromResponse(response)
							: undefined;
					if (wrongTenant) {
						// This host's session belongs to another tenant. Sign that
						// cookie out and show this tenant's login rather than bouncing
						// the browser to the other org.
						console.warn("Session does not belong to this tenant, signing out");
						await signOutAndReload();
						return hangUntilUnload();
					}

					if (status === 401 || status === 403) {
						console.warn(
							"Unauthenticated or forbidden response, redirecting to login",
						);
						redirectToLogin();
					}

					return response;
				} catch (error) {
					const duration = Date.now() - start;
					console.error("API Request Failed", {
						method,
						pathname,
						duration,
						error: error instanceof Error ? error.message : String(error),
					});

					throw error;
				}
			},
		});

		return createORPCClient(link);
	}, []);

	return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApiClient(): ApiClient {
	const client = useRequiredContext(ApiContext);
	return client;
}

function hangUntilUnload(): Promise<Response> {
	return new Promise(() => {});
}

function redirectToLogin() {
	if (!window.location.pathname.endsWith("/login")) {
		window.location.href = absoluteRoute(Routes.login);
	}
}

async function wrongTenantErrorFromResponse(
	response: Response,
): Promise<ReturnType<typeof parseWrongTenantError>> {
	try {
		return parseWrongTenantError(await response.clone().json());
	} catch {
		// An unreadable body is not a WRONG_TENANT we can act on. Falling through
		// leaves the caller with its 409 instead of logging the user out.
		return undefined;
	}
}
